import { MutationCache, QueryClient, type Query } from '@tanstack/react-query';
import { showToast } from './hooks/useToast';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /**
       * `false` for a mutation whose caller already tells the user it failed, so the app-wide fallback
       * toast in `createDefaultQueryClient` does not show a second one.
       */
      errorToast?: boolean;
    };
  }
}

type QueryTimingOptions = {
  gcTime?: number;
  staleTime?: number | 'static' | ((query: Query) => number | 'static' | undefined);
};

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const getTimingOptions = (options: unknown): QueryTimingOptions => options as QueryTimingOptions;

const resolveStaleTime = (staleTime: QueryTimingOptions['staleTime'], query: Query): number | undefined => {
  if (typeof staleTime === 'function') {
    return resolveStaleTime(staleTime(query), query);
  }

  if (staleTime === 'static') {
    return Number.POSITIVE_INFINITY;
  }

  return staleTime;
};

const getEffectiveStaleTime = (queryClient: QueryClient, query: Query) => {
  const defaultStaleTime =
    resolveStaleTime(getTimingOptions(queryClient.getQueryDefaults(query.queryKey)).staleTime, query) ?? 0;
  const observerStaleTimes = query.observers
    .map((observer) => resolveStaleTime(observer.options.staleTime, query))
    .filter(isDefined);

  if (query.getObserversCount() > 0 && observerStaleTimes.length > 0) {
    return Math.min(...observerStaleTimes);
  }

  return resolveStaleTime(getTimingOptions(query.options).staleTime, query) ?? defaultStaleTime;
};

const getEffectiveGcTime = (queryClient: QueryClient, query: Query) => {
  const defaultGcTime = getTimingOptions(queryClient.getQueryDefaults(query.queryKey)).gcTime ?? 0;
  const observerGcTimes = query.observers.map((observer) => observer.options.gcTime).filter(isDefined);

  if (query.getObserversCount() > 0 && observerGcTimes.length > 0) {
    return Math.min(...observerGcTimes);
  }

  return getTimingOptions(query.options).gcTime ?? defaultGcTime;
};

const shouldInvalidateQuery = (queryClient: QueryClient, query: Query) => {
  const staleTime = getEffectiveStaleTime(queryClient, query);
  const gcTime = getEffectiveGcTime(queryClient, query);

  return staleTime !== Number.POSITIVE_INFINITY || gcTime !== Number.POSITIVE_INFINITY;
};

export const createDefaultQueryClient = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Route loaders prefetch on hover and navigation; with React Query's default of 0 the data
        // they fetched was already stale when the page mounted, and was fetched a second time. Every
        // mutation still invalidates the cache (`MutationCache.onSuccess` below), so the user's own
        // changes show at once; this only delays changes made elsewhere (another tab or user).
        // Queries that must always be fresh set `staleTime: 0` themselves.
        staleTime: 20_000,
        retry: 2,
        // Only a query with nothing to show goes to the route error page. A background refetch that
        // fails (window focus, invalidation after a mutation) keeps the data already on screen
        // instead of replacing the whole page.
        throwOnError: (_error, query) => query.state.data === undefined,
      },
      mutations: {
        // No `retry`: mutations are not idempotent, and a request that succeeded on the server but
        // lost its response would be sent again (a second project, a second invitation).
        // No `throwOnError` either: rethrowing to the route error page replaced the whole page, form
        // input included, even where the caller had already shown its own error toast. A failure is
        // reported by the `onError` below instead.
      },
    },
    mutationCache: new MutationCache({
      onError: (_error, _variables, _onMutateResult, mutation) => {
        if (mutation.meta?.errorToast === false) {
          return;
        }
        showToast('Something went wrong', 'The change could not be saved. Please try again.', 'error');
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          predicate: (query) => shouldInvalidateQuery(queryClient, query),
        });
      },
    }),
  });

  return queryClient;
};
